/**
 * 身份信息页面配置（identityConfig.js）
 * -----------------------------------------------------------------------------
 * 该文件导出一个数组，每个元素代表一种主体身份类型在"修改资料"页面中需要渲染的表单字段配置。
 * 页面根据当前登录用户的 `identityType`（后台返回）来选择对应的配置项进行展示。
 *
 * 数组元素结构：
 * {
 *   type: Number,            // 身份类型编码（2=农户，3=集体经济组织，4=新型经营主体，9=农资供应商，后续可拓展）
 *   fields: FieldConfig[]    // 当前身份类型表单字段列表
 * }
 *
 * FieldConfig 统一字段说明：
 * ┌────────────────────────────────────────────────────────────────────────────┐
 * │  属性              │  说明                                               │
 * ├────────────────────┼─────────────────────────────────────────────────────┤
 * │ label              │ 显示在表单左侧的字段名称                               │
 * │ key                │ 表单字段 `model` 对应的键，也是接口字段名               │
 * │ type               │ 字段类型：'text' | 'image' | 'cascader' | 'double-cascader' 等 │
 * │ accept             │ 上传类型限制，仅 `type === 'image'` 时生效               │
 * │ style              │ 对图片上传组件的样式标识：'businessLicense' | 'card' | 'cardBack'│
 * │ multiple           │ 是否支持多文件上传                                     │
 * │ required           │ 是否必填，用于简写校验规则                               │
 * │ readonly           │ 是否只读，适用于后台下发不可编辑字段                     │
 * │ changeable         │ 输入框旁是否出现 "变更" 按钮（true 表示展示）             │
 * │ btnStyle           │ 按钮样式枚举：'changeType'(变更)、'smsCode'(发送验证码) 等  │
 * │ keyBtnType         │ 用来区分同一按钮类型下不同业务（如 NhphoneType 等）         │
 * │ smsCodeType        │ 短信验证码类型，后台接口区分用                            │
 * │ rules              │ Element-UI 表单校验规则数组                               │
 * │ props1 / props2    │ `double-cascader` 级联组件的自定义 props                 │
 * │ cityOptions        │ 单选/多选下拉固定数据（示例：示范合作社级别）              │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * 新增主体类型步骤：
 * 1. 在数组末尾追加一个对象，并将 `type` 修改为后台约定的新编码。
 * 2. 按照"展示顺序"依次编写其 `fields` 数组，遵循上表字段含义。
 * 3. 若需新增未支持的 `type` 或按钮行为，先在表单渲染组件中实现对应功能，再在此处配置。
 *
 * 注意：
 * - 所有正则或校验提示请写在 rules 中，避免散落在组件逻辑里。
 * - 图片类字段务必指定 `style` 以便上传组件正确选择模板。
 * - 如需国际化，将 label 文案抽离至 i18n 资源文件。
 */
export default [

    {
        type: 2, // 农户
        fields: [
            {
                label: "身份证人像面：", key: "cardFront", type: "image", accept: "image/*", style: 'card', required: true,
                rules: [{ required: true, message: '请上传身份证人像面', trigger: 'change', type: 'array' }]
            },
            {
                label: "身份证国徽面：", key: "cardBack", type: "image", accept: "image/*", style: "cardBack", required: true,
                rules: [{ required: true, message: '请上传身份证国徽面', trigger: 'change', type: 'array' }]
            },
            { label: "姓名：", key: "realName", type: "text", readonly: true, required: true },
            { label: "身份证号：", key: "cardId", type: "text", readonly: true, required: true },
            { label: "手机号：", key: "userName", type: "text", changeable: false, btnStyle: 'changeType', keyBtnType: 'NhphoneType', readonly: true, required: true },
            { key: "smsCode" },
            {
                label: "地址：",
                key: "areaCode",
                type: "double-cascader",
                required: true,
            },
            {
                label: "银行卡号", key: "bankCardId", type: "text", btnStyle: 'changeType', keyBtnType: 'NhbankCardIdType', smsCodeType: '4', readonly: true, required: true,
                rules: [
                    { required: true, message: '请输入银行卡号', trigger: 'blur' },
                    { pattern: /^\d{12,19}$/, message: '银行卡号格式不正确', trigger: 'blur' }
                ]
            },
            { label: "所属银行", key: "affiliatedBank", type: "text" },
            {
                label: "银行预留手机号", key: "bankCardPhone", type: "text", required: true,
                rules: [
                    { required: true, message: '请输入银行预留手机号', trigger: 'blur' },
                    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
                ]
            },
            // { label: "银行短信验证码", key: "smsCode", type: "text" , btnStyle: 'bankPhoneBtn',Btntit:'变更',Btntype:'info' }

        ],

    },
    {
        type: 4, // 新型经营主体
        fields: [
            {
                label: "营业执照", key: "businessLicense", type: "image", accept: "image/*", style: "businessLicense", required: true,
                rules: [{ required: true, message: '请上传营业执照', trigger: 'change', type: 'array' }]
            },
            { label: "公司名称：", key: "companyName", type: "text", changeable: true, required: true },
            { label: "统一社会信用代码：", key: "socialCreditId", type: "text", changeable: false, readonly: true, required: true },
            { key: 'centerPoint' },//这是坐标
            {
                label: "组织所在地：",
                key: "areaCode",
                type: "double-cascader",
                required: true,
            },
            { label: "对公账号：", key: "companyBankId", type: "text", },

            { label: "所属银行：", key: "affiliatedBank", type: "text", },
            {
                label: "法人身份证人像面：", key: "bossCardFront", type: "image", accept: "image/*", style: 'card', required: true,
                rules: [{ required: true, message: '请上传法人身份证人像面', trigger: 'change', type: 'array' }]
            },
            {
                label: "法人身份证国徽面：", key: "bossCardBack", type: "image", accept: "image/*", style: 'cardBack', required: true,
                rules: [{ required: true, message: '请上传法人身份证国徽面', trigger: 'change', type: 'array' }]
            },
            { label: "法人/投资人姓名：", key: "bossName", type: "text", required: true },
            { label: "法人/投资人身份证号：", key: "bossCardId", type: "text", required: true },
            {
                label: "经营场所照片：", key: "companyPhoto", type: "image", accept: "image/*", multiple: true,
                // rules: [{ required: true, message: '请上传经营场所照片', trigger: 'change', type: 'array' }]
            },
            { label: "联系人：", key: "contactName", type: "text", btnStyle: 'changeType', keyBtnType: 'fourContactName', readonly: true, required: true },
            { key: "contactCard", },
            {
                label: "联系人电话：", key: "contactPhone", type: "text", required: true, readonly: true,
                rules: [{ required: true, message: '请输入联系人电话', trigger: 'blur' },
                { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }]
            },
            { key: "contactSmsCode", },
            { label: "手机号：", key: "userName", type: "text", changeable: false, btnStyle: 'changeType', keyBtnType: 'NhphoneType', readonly: true, required: true },
            // {
            //     label: "示范合作社级别",
            //     key: "city",
            //     type: "cascader",
            //     required: true,
            //     cityOptions: [
            //         {
            //             label: '国家级示范合作社',
            //             value: '国家级示范合作社'
            //         },
            //         {
            //             label: '省级示范合作社',
            //             value: '省级示范合作社'
            //         },
            //         {
            //             label: '市级示范合作社',
            //             value: '市级示范合作社'
            //         },
            //         {
            //             label: '县级示范合作社',
            //             value: '县级示范合作社'
            //         },
            //         {
            //             label: '非示范合作社',
            //             value: '非示范合作社'
            //         },
            //     ]
            // },
        ],

    },
    {
        type: 3, // 集体经济组织
        fields: [
            {
                label: "集体经济组织登记证", key: "businessLicense", type: "image", accept: "image/*", style: "businessLicense", required: true,
                rules: [{ required: true, message: '请上传登记证', trigger: 'change', type: 'array' }]
            },
            { label: "组织名称", key: "groupName", type: "text", required: true },
            { label: "统一社会信用代码", key: "socialCreditId", type: "text", readonly: true, required: true },
            {
                label: "组织所在地：",
                key: "areaCode",
                type: "double-cascader",
                required: true,
            },
            { label: "对公账号", key: "groupBankId", type: "text", changeable: true },
            { label: "所属银行", key: "affiliatedBank", type: "text", changeable: true },
            {
                label: "负责人身份证人像面", key: "bossCardFront", type: "image", accept: "image/*", style: 'card', required: true,
                rules: [{ required: true, message: '请上传身份证人像面', trigger: 'change', type: 'array' }]
            },
            {
                label: "负责人身份证国徽面", key: "bossCardBack", type: "image", accept: "image/*", style: 'cardBack', required: true,
                rules: [{ required: true, message: '请上传身份证国徽面', trigger: 'change', type: 'array' }]
            },
            { label: "负责人姓名", key: "leaderName", type: "text", changeable: true, required: true },
            { label: "负责人身份证号", key: "leaderCardId", type: "text", changeable: true, required: true },
            { label: "联系人：", key: "contactName", type: "text", btnStyle: 'changeType', keyBtnType: 'fourContactName', readonly: true, required: true },
            { label: "联系人电话", key: "contactPhone", type: "text", changeable: true, required: true },
            { key: "contactSmsCode", },
            { key: "contactCard", },
        ],

    },
    {
        type: 9, //农资供应商
        fields: [
            { key: 'centerPoint' },//这是坐标
            {
                label: "营业执照", key: "businessLicense", type: "image", accept: "image/*", style: "businessLicense", required: true,
                rules: [{ required: true, message: '请上传营业执照', trigger: 'change', type: 'array' }]
            },
            { label: "公司名称：", key: "companyName", type: "text", changeable: true, required: true },
            { label: "统一社会信用代码：", key: "socialCreditId", type: "text", readonly: true, required: true },

            {
                label: "组织所在地：",
                key: "areaCode",
                type: "double-cascader",
                required: true,
                // props1: { label: "areaName", children: "children", value: "id", checkStrictly: true, expandTrigger: "hover" },
                // props2: { label: "areaName", children: "children", value: "id", checkStrictly: true, expandTrigger: "hover" },
            },
            { label: "对公账号：", key: "companyBankId", type: "text", required: true, },
            { label: "所属银行：", key: "affiliatedBank", type: "text", },
            {
                label: "法人身份证人像面：", key: "bossCardFront", type: "image", accept: "image/*", style: 'card', required: true,
                rules: [{ required: true, message: '请上传身份证人像面', trigger: 'change', type: 'array' }]
            },
            {
                label: "法人身份证国徽面：", key: "bossCardBack", type: "image", accept: "image/*", style: 'cardBack', required: true,
                rules: [{ required: true, message: '请上传身份证国徽面', trigger: 'change', type: 'array' }]
            },
            { label: "法人/投资人姓名：", key: "bossName", type: "text", required: true },
            { label: "法人/投资人身份证号：", key: "bossCardId", type: "text", required: true },
            {
                label: "经营场所照片：", key: "companyPhoto", type: "image", accept: "image/*", multiple: true, required: true,
                rules: [{ required: true, message: '请上传经营场所照片', trigger: 'change', type: 'array' }]
            },
            { label: "联系人：", key: "contactName", type: "text", btnStyle: 'changeType', keyBtnType: 'fourContactName', readonly: true, required: true },
            { label: "联系人身份证号：", key: "contactCard", type: "text", required: false },
            { label: "联系人电话：", key: "contactPhone", type: "text", readonly: true, required: true },
            { key: "contactSmsCode" }
        ],

    },
    // 新增：其他类型（type: 0）
    {
        type: "error", // 其他类型
        fields: [
            { label: "姓名：", key: "identityName", type: "text", required: true, readonly: true },
            { label: "手机号：", key: "userName", type: "text",  btnStyle: 'changeType', keyBtnType: 'NhphoneType', readonly: true, required: true  },
            { label: "角色：", key: "identityTypeDesc", type: "text", required: true, readonly: true },
        ],
    },
]